import { format } from 'date-fns';

interface GanttHeaderProps {
  dates: Date[]; // Array of dates to display
  pixelsPerDay: number; // How many pixels each day takes
  zoomLevel: 'hour' | 'day' | 'week' | 'month';
}

export function GanttHeader({ dates, pixelsPerDay, zoomLevel }: GanttHeaderProps) {
  const today = format(new Date(), 'yyyy-MM-dd');

  // For hour view, render hours instead of just days
  if (zoomLevel === 'hour') {
    const pixelsPerHour = pixelsPerDay / 24;
    return (
      // z-40 sits above the rows' internal z-30/z-40 as belt-and-braces on top of
      // the `isolate` on each row root, so even a future un-isolated row can't
      // paint over the header (WRK-5717).
      <div className="flex border-b-2 border-border bg-background sticky top-0 z-40">
        {/* Team name column spacer - sticky */}
        <div className="w-[200px] flex-shrink-0 border-r-2 border-border bg-muted px-4 py-3 font-semibold text-sm sticky left-0 z-30">
          Team
        </div>

        {/* Time columns with hours */}
        <div className="flex">
          {dates.map((date) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const isToday = dateStr === today;
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            return (
              <div key={dateStr} className="flex-shrink-0 border-r-2 border-border">
                {/* Day header */}
                <div
                  className={`px-2 py-1 text-center text-xs font-semibold border-b border-border ${
                    isToday
                      ? 'bg-blue-100 text-blue-900 dark:bg-blue-950/50 dark:text-blue-300'
                      : isWeekend
                        ? 'bg-slate-100 dark:bg-slate-800/40'
                        : 'bg-muted/50'
                  }`}
                  style={{ width: `${pixelsPerDay}px` }}
                >
                  <div>{format(date, 'EEE, MMM d')}</div>
                </div>

                {/* Hour columns */}
                <div className="flex">
                  {Array.from({ length: 24 }, (_, hour) => {
                    const currentHour = new Date().getHours();
                    const isCurrentHour = isToday && hour === currentHour;

                    return (
                      <div
                        key={hour}
                        className={`flex-shrink-0 border-r border-border/30 px-1 py-2 text-center text-[10px] font-medium relative ${
                          isCurrentHour
                            ? 'bg-blue-50 text-blue-900 dark:bg-blue-950/30 dark:text-blue-300'
                            : isWeekend
                              ? 'bg-slate-100/40 dark:bg-slate-800/30'
                              : 'bg-card'
                        }`}
                        style={{ width: `${pixelsPerHour}px` }}
                      >
                        <div>{hour.toString().padStart(2, '0')}:00</div>
                        {/* Current hour indicator line */}
                        {isCurrentHour && (
                          <div className="absolute inset-x-0 top-0 bottom-0 w-0.5 bg-blue-500 pointer-events-none mx-auto" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Default day/week/month view
  return (
    // z-40 (above the rows' internal z-30/z-40) is belt-and-braces on top of the
    // `isolate` on each row root: even an un-isolated future row can't paint over
    // the header (WRK-5717).
    <div className="flex border-b-2 border-border bg-background sticky top-0 z-40">
      {/* Team name column spacer - sticky */}
      <div className="w-[200px] flex-shrink-0 border-r-2 border-border bg-muted px-4 py-3 font-semibold text-sm sticky left-0 z-30">
        Team
      </div>

      {/* Time columns */}
      <div className="flex">
        {dates.map((date, index) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const isToday = dateStr === today;
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;

          return (
            <div
              key={dateStr}
              className={`flex-shrink-0 border-r border-border px-2 py-3 text-center text-xs font-medium relative ${
                isToday
                  ? 'bg-blue-50 text-blue-900 dark:bg-blue-950/30 dark:text-blue-300'
                  : isWeekend
                    ? 'bg-slate-100/60 dark:bg-slate-800/40'
                    : 'bg-card'
              }`}
              style={{ width: `${pixelsPerDay}px` }}
            >
              <div className="font-semibold">{format(date, 'EEE')}</div>
              <div className="text-base font-bold mt-1">{format(date, 'd')}</div>
              {index === 0 || date.getDate() === 1 ? (
                <div className="text-[10px] text-muted-foreground mt-1">{format(date, 'MMM')}</div>
              ) : null}

              {/* Today indicator line */}
              {isToday && (
                <div className="absolute inset-x-0 top-0 bottom-0 w-0.5 bg-blue-500 pointer-events-none mx-auto" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
